// The full Communications section under Company (N-094, "forward and
// file") — list, then detail, the same idiom CompanyVendors.tsx already
// uses for its own list-then-card drill-in, lifted the same way: the id
// lives in CompanyPage's own hash-derived state (`hashToCompany`'s
// `comm-<id>` pattern, mirroring its existing `vendor-<id>`).
import { followInAppLink } from '../lib/router';
import type { JSX } from 'preact';
import { webLink } from '../lib/webLink';
import { safeHtml } from '../lib/safeHtml';
import { useEffect, useRef, useState } from 'preact/hooks';
import { EmptyState, FaultState } from './FaultState';
import { useResource, refetchResource } from '../lib/store';
import { formatClinicDateTime } from '../lib/reportSchedules';
import { session } from '../lib/session';
import {
  COMMUNICATIONS_KEY, fetchCommunications, communicationDetailKey, fetchCommunicationDetail,
  markCommunicationRead, setCommunicationMustRead, showOlderCommunications,
  type CommunicationsData, type CommunicationDetailData,
} from '../lib/communications';

const EM_DASH = '—';

/** Same gate as insuranceRoleGate/leasesRoleGate — OWNER/DIRECTOR only,
 * never while wearing the manager lens (server-enforced,
 * communicationMustReadGate in src/intake_routes.js; this is only the UI
 * half — a manager never even sees the chip). */
function canSetMustRead(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR'));
}

/** Owner signed in as himself (server: setCommunicationDeskRetired). */
export function isOwnerViewer(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && me.role === 'OWNER');
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function CompanyCommunications(
  { commId, onOpen, onBack }:
  { commId: number | null; onOpen: (id: number) => void; onBack: () => void },
): JSX.Element {
  if (commId !== null) {
    return <CommunicationDetail id={commId} onBack={onBack} />;
  }
  return <CommunicationsList onOpen={onOpen} />;
}

function CommunicationsList({ onOpen }: { onOpen: (id: number) => void }): JSX.Element {
  const load = useResource<CommunicationsData>(COMMUNICATIONS_KEY, fetchCommunications);
  const [olderState, setOlderState] = useState<'idle' | 'loading' | string>('idle');

  if (load.status === 'loading') return <EmptyState label="Loading communications…" />;
  if (load.status === 'fault') return <FaultState name="communications_load_failed" detail={load.error} />;

  const items = [...load.data.communications].sort((a, b) => {
    const ta = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
    const tb = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
    return tb - ta;
  });
  const unreadCount = load.data.unreadCount;
  const olderCount = load.data.olderCount ?? 0;

  async function showOlder(): Promise<void> {
    setOlderState('loading');
    try {
      await showOlderCommunications();
      setOlderState('idle');
    } catch (err) {
      setOlderState(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div class="card" data-testid="company-communications">
      <div class="section-head">
        <h2>Communications</h2>
        {/* Cursor review #61 (30 days since 2026-11-02): the last 30 days by default, plus anything
           older that is still unread or must-read. unreadCount is always
           counted across everything. */}
        <p>
          {olderCount
            ? `${items.length} shown (last 30 days, plus anything unread or must-read), ${olderCount} older, ${unreadCount} unread.`
            : `${items.length} total, ${unreadCount} unread.`}
        </p>
      </div>
      {items.length === 0 ? (
        <p class="empty">No communications yet.</p>
      ) : (
        <div class="table-wrap">
          <table class="roster-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>From</th>
                <th>Received</th>
                <th>Attachments</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.id}
                  class="vendor-row"
                  data-testid={`communication-row-${c.id}`}
                  data-unread={c.unread ? 'true' : 'false'}
                  onClick={() => onOpen(c.id)}
                >
                  <td>
                    {c.unread ? <b>{c.subject || '(no subject)'}</b> : (c.subject || '(no subject)')}
                    {c.mustRead && c.unread ? <span class="badge warn">MUST READ</span> : null}
                    {c.deskRetired && isOwnerViewer() ? <span class="badge" data-testid={`communication-retired-${c.id}`}>Retired from Desk</span> : null}
              {c.draft ? <span class="badge">DRAFT</span> : c.whatsNew ? <span class="badge">WHAT'S NEW</span> : null}
                  </td>
                  <td>{c.fromName || c.fromAddress || EM_DASH}</td>
                  <td>{c.receivedAt ? formatClinicDateTime(c.receivedAt) : EM_DASH}</td>
                  <td>{c.attachmentCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {olderCount ? (
        <div class="section-head-actions">
          <button
            type="button"
            class="btn"
            data-testid="communications-show-older"
            disabled={olderState === 'loading'}
            onClick={() => { void showOlder(); }}
          >
            {olderState === 'loading' ? 'Loading…' : `Show older (${olderCount})`}
          </button>
          {olderState !== 'idle' && olderState !== 'loading'
            ? <FaultState name="communications_older_load_failed" detail={olderState} />
            : null}
        </div>
      ) : null}
    </div>
  );
}

function CommunicationDetail({ id, onBack }: { id: number; onBack: () => void }): JSX.Element {
  const key = communicationDetailKey(id);
  const load = useResource<CommunicationDetailData>(key, () => fetchCommunicationDetail(id));
  // Guards against double-marking on a re-render — this effect fires once
  // per successful load of THIS id, not once per render.
  const markedFor = useRef<number | null>(null);
  const [mustReadBusy, setMustReadBusy] = useState(false);

  useEffect(() => {
    if (load.status !== 'ready') return;
    if (!load.data.communication.unread) return;
    if (markedFor.current === id) return;
    markedFor.current = id;
    void markCommunicationRead(id).then(() => {
      void refetchResource(COMMUNICATIONS_KEY, fetchCommunications);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load.status, id]);

  if (load.status === 'loading') {
    return (
      <div class="main">
        <button type="button" class="link" onClick={onBack}>Back to Communications</button>
        <EmptyState label="Loading communication…" />
      </div>
    );
  }
  if (load.status === 'fault') {
    return (
      <div>
        <button type="button" class="link" onClick={onBack}>Back to Communications</button>
        <FaultState name="communication_load_failed" detail={load.error} />
      </div>
    );
  }

  const { communication, readBy } = load.data;
  const isOwner = session.value.me?.role === 'OWNER';
  const canToggleMustRead = canSetMustRead();

  async function toggleMustRead(): Promise<void> {
    if (mustReadBusy) return;
    setMustReadBusy(true);
    try {
      await setCommunicationMustRead(id, !communication.mustRead);
      await Promise.all([
        refetchResource(key, () => fetchCommunicationDetail(id)),
        refetchResource(COMMUNICATIONS_KEY, fetchCommunications),
      ]);
    } finally {
      setMustReadBusy(false);
    }
  }

  return (
    <div class="person-profile" data-testid="communication-detail">
      <div class="page-header">
        <button type="button" class="link" data-testid="communication-back" onClick={onBack}>
          Back to Communications
        </button>
      </div>
      <section class="card">
        <div class="section-head">
          <h2>
            {communication.subject || '(no subject)'}
            {communication.mustRead ? <span class="badge warn">MUST READ</span> : null}
          </h2>
          <p>
            {communication.fromName || communication.fromAddress || 'Unknown sender'}
            {' · '}
            {communication.receivedAt ? formatClinicDateTime(communication.receivedAt) : EM_DASH}
          </p>
          {canToggleMustRead ? (
            <p>
              <button
                type="button"
                class="btn"
                data-testid="communication-must-read-toggle"
                disabled={mustReadBusy}
                onClick={() => void toggleMustRead()}
              >
                {communication.mustRead ? 'Clear must read' : 'Mark must read'}
              </button>
              {communication.mustRead && communication.mustReadSetBy ? (
                <span class="muted">
                  {' '}
                  — set by {communication.mustReadSetBy}
                  {communication.mustReadSetAt ? ` on ${formatClinicDateTime(communication.mustReadSetAt)}` : ''}
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
        <div
          class="communication-body"
          data-testid="communication-body"
          // A What's New links to places in the app — open them in place.
          onClick={followInAppLink}
          // Cleaned again here with a real parser and an allow-list (Cursor
          // review 2026-10-15): the server's regex cleaner is not enough.
          dangerouslySetInnerHTML={{ __html: safeHtml(communication.bodyHtml || '') || '<p><em>No message body.</em></p>' }}
        />
      </section>
      <section class="card" data-testid="communication-attachments">
        <div class="section-head"><h2>Attachments</h2></div>
        {communication.attachments.length === 0 ? (
          <p class="empty">No attachments.</p>
        ) : (
          <ul class="nhp-pack">
            {communication.attachments.map((a, i) => (
              // eslint-disable-next-line react/no-array-index-key -- attachments carry no id of their own
              <li key={i} class="nhp-pack-row">
                {webLink(a.url) ? (
                  <a class="doc-link" href={webLink(a.url)!} target="_blank" rel="noreferrer">{a.name}</a>
                ) : a.name}
                {' '}
                <span class="muted">
                  {a.mimeType}
                  {a.size != null ? ` · ${formatBytes(a.size)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        {communication.emlDriveUrl ? (
          <p><a href={communication.emlDriveUrl} target="_blank" rel="noreferrer">View original email in Drive</a></p>
        ) : null}
      </section>
      {isOwner && readBy !== null ? (
        <section class="card" data-testid="communication-read-by">
          <div class="section-head"><h2>Read by</h2></div>
          {readBy.length === 0 ? (
            <p class="empty">No one has opened this yet.</p>
          ) : (
            <ul class="nhp-pack">
              {readBy.map((r) => (
                <li key={r.email} class="nhp-pack-row">{r.email} — {formatClinicDateTime(r.readAt)}</li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}
